import {
  Component,
  Input,
  OnInit
} from '@angular/core';
import { OnyxEvent } from '../../models/event';
import { Payment } from '../../models/payment';
import { ErrorHandlerService } from '../../../shared/utils/error-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { PaymentStatusUtils } from 'app/event/utils/payment-status-utils';
import { EventStore } from 'app/event/service/event.store';
import { DropdownItem } from 'app/event/models/dropdown-item';
import { PaymentStore } from 'app/event/service/payment.store';
import { Observable } from 'rxjs/Observable';
import { AuthenticationStore } from 'app/permission/services/authentication.store';
import { UpperCasePipe } from '@angular/common';
import { PaymentType } from 'app/event/models/payment-type';
import { NonCommissionType } from 'app/event/models/non-commission-type';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { PaymentStatus } from 'app/event/models/payment-status.enum';
import { PioneerValidators } from 'app/shared/utils/pioneer.validators';
import { Party } from '../../models/party';
import { AdjustmentStore } from 'app/event/service/adjustment.store';
import { EventUtils } from '../../utils/event-utils';
import { DateUtil } from '../../../shared/utils/date.util';
import { Unsubscriber } from 'app/shared/helper/unsubscriber';
import { PaymentSummary } from 'app/event/models/payment-summary/payment-summary';
import { PaymentSummaryStore } from 'app/event/service/payment-summary/payment-summary.store';

@Component({
  selector: 'pioneer-payment-summary',
  templateUrl: './payment-summary.component.html'
})
export class PaymentSummaryComponent extends Unsubscriber implements OnInit {

  @Input()
  vendor: Party;

  paymentSummary: PaymentSummary = null;
  @Input() prepayment: Payment = null;

  nonCommissionTypes: DropdownItem[];
  cancelTypes: DropdownItem[];
  paidDirectTypes: DropdownItem[];

  private isLastPaymentRequestedOrRequestResentOrRequestRejected: boolean;
  isLastPaymentPayRejected: boolean;

  paymentForm: FormGroup;

  isPropertyUser: boolean;

  onyxEvent: OnyxEvent;

  isEventFinished = false;

  private latestFinalPayment: Payment = null;

  ui = {
    showRejectedMessage: false,
    strikeThroughPaymentLines: false,
    strikeThroughFinalPayment: false,
    strikeThroughPrepayment: false
  };

  calculatedPaymentWithTax: number;

  constructor(
    private errorHandlerService: ErrorHandlerService,
    private translateService: TranslateService,
    private eventStore: EventStore,
    private paymentStore: PaymentStore,
    private adjustmentStore: AdjustmentStore,
    private authenticationStore: AuthenticationStore,
    private uppercasePipe: UpperCasePipe,
    private formBuilder: FormBuilder,
    private paymentSummaryStore: PaymentSummaryStore,
  ) {
    super();
  }

  @Input()
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit(): void {

    this.isPropertyUser = this.authenticationStore.isPropertyUser();

    this.isLastPaymentPayRejected = false;

    if (!this.onyxEvent) {
      this.errorHandlerService.throw(
        'Input property onyxEvent is null in PaymentSummaryComponent',
        this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
          property: 'onyxEvent',
          className: 'PaymentSummaryComponent'
        })
      );
    }

    if (!this.vendor || !this.vendor.eventPartyId) {
      const message = this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
        property: 'vendor.eventPartyId',
        className: 'PaymentSummaryComponent'
      });
      this.errorHandlerService.throw('vendor.eventPartyId is null or undefined', message);
      return;
    }

    this.paymentSummaryStore.getPaymentSummary(this.vendor.eventPartyId)
      .pipe(
        takeUntil(this._destroyed$),
        catchError(err => {
          this.errorHandlerService.throw(err.message, this.translateService.instant('MESSAGES.ERROR.COULD_NOT_GET_PAYMENT_SUMMARY'));
          return Observable.empty();
        })
      )
      .subscribe((paymentSummary: PaymentSummary) => {
        this.paymentSummary = paymentSummary;

        const finalPaymentStatus = this.paymentSummary ? this.paymentSummary.paymentStatus : undefined;
        this.computePaymentStatus(finalPaymentStatus);
        this.isLastPaymentRequestedOrRequestResentOrRequestRejected =
          this.paymentStore.isRequestedOrResentRequestOrRequestRejectedOrError(paymentSummary);
      });

    if (this.prepayment) {
      if (PaymentStatusUtils.isError(this.prepayment.status)) {
        this.ui.showRejectedMessage = true;
        this.ui.strikeThroughPaymentLines = true;
        this.ui.strikeThroughPrepayment = true;
      }
    }

    this.addNonCommissionDueTypes();
    this.addPaidDirectTypes();
    this.addCancelTypes();

    this.paymentStore.isLastVendorPaymentPayRejected(this.vendor.eventPartyId).subscribe(isPayRejected => {
      this.isLastPaymentPayRejected = isPayRejected;
    });

    this.paymentForm = this.formBuilder.group({
      'description': [ '', Validators.maxLength(255) ],
      'type': [ null, ],
      'commissionAmount': [ null, [ PioneerValidators.isValidAmount ]]
    });

    this.paymentStore.getVendorFinalPayment(this.vendor.eventPartyId)
      .take(1)
      .subscribe((latestFinalPayment: Payment) => this.latestFinalPayment = latestFinalPayment);

    this.checkEventDates();

    this.paymentStore.getCalculatedPaymentWithTax(this.vendor.eventPartyId).pipe(
      takeUntil(this._destroyed$),
      tap((calculatedPaymentWithTax: number) => {
        this.calculatedPaymentWithTax = calculatedPaymentWithTax;
        this.paymentForm.setValidators([PioneerValidators.commissionAmountAndTypeCrossValidator(this.calculatedPaymentWithTax)]);
        this.paymentForm.updateValueAndValidity();
      })
    ).subscribe();
  }

  private computePaymentStatus(finalPaymentStatus: PaymentStatus) {
    const isErrorFinalPayment = PaymentStatusUtils.isError(finalPaymentStatus);
    if (isErrorFinalPayment) {
      this.ui.showRejectedMessage = true;
      this.ui.strikeThroughPaymentLines = true;
      this.ui.strikeThroughFinalPayment = true;
    }
  }

  toggleMasterBillPaidSetting(): void {
    for (let i = 0; i < this.onyxEvent.eventVendors.length; i++) {
      let vendor = this.onyxEvent.eventVendors[i];
      const vendorCode = vendor.code ? vendor.code : vendor.chain + '|' + vendor.brand + '|' + vendor.propertyId;

      if  (this.getVendorCode() === vendorCode) {
        vendor.masterBillPaid = !vendor.masterBillPaid;
      }
    }
    this.eventStore.updateEventIndicators(this.onyxEvent).subscribe();
  }

  getVendorCode(): string {
    return this.vendor.code ? this.vendor.code : this.vendor.chain + '|' + this.vendor.brand + '|' + this.vendor.propertyId;
  }

  showPaymentForm(): Observable<boolean> {
    return this.paymentStore
      .isLastVendorPaymentRequestedOrRequestResentOrRequestRejected(this.vendor.eventPartyId)
      .map((showForm) => {
        return showForm && this.showPayRejected() && !this.vendor.subscriber;
      });
  }

  public isAuthorizedPayment() {
    return this.authenticationStore.hasPermission('AuthorizePayment') && this.isEventControlledByThisUserType();
  }

  getAmountPaidLabel(): string {
    const label = 'EVENT.COMMISSION.NON_COMM_AMOUNT_PAID';
    const params = {
      currency: this.uppercasePipe.transform(this.onyxEvent.currency)
    };
    return this.translateService.instant(label, params);
  }

  isPayDirect(): boolean {
    return this.nonCommType && this.nonCommType.key === PaymentType.PAID_DIRECT;
  }

  addNonCommissionDueTypes(): void {
    this.nonCommissionTypes = [
      new DropdownItem(PaymentType.PAID_DIRECT, NonCommissionType.PAID_DIRECT),
      new DropdownItem(PaymentType.EVENT_CANCELLED, NonCommissionType.EVENT_CANCELLED)
    ];
  }

  addPaidDirectTypes(): void {
    this.paidDirectTypes = [
      new DropdownItem(PaymentType.PAID_DIRECT, NonCommissionType.PAID_DIRECT)
    ];
  }

  addCancelTypes(): void {
    this.cancelTypes = [
      new DropdownItem(PaymentType.EVENT_CANCELLED, NonCommissionType.EVENT_CANCELLED)
    ];
  }

  get nonCommType(): DropdownItem {
    return this.getFormControlValue('type');
  }

  get nonCommAmountPaid(): number {
    return this.getFormControlValue('commissionAmount');
  }

  private getFormControlValue(name: string): any {
    return this.paymentForm ? this.paymentForm.get(name).value : null;
  }

  isFullAgencyNonCommSelected(): boolean {
    return this.authenticationStore.hasPermission('AgencyEventLevelIndicators') && this.isNonCommTypeSelected();
  }

  private isNonCommTypeSelected(): boolean {
    return this.nonCommType != null;
  }

  unSelectNonCommType(): void {
    this.paymentForm.reset({
      description: this.paymentForm.get('description').value,
      type: null,
      commissionAmount: null
    });
  }

  public isEventControlledByThisUserType() {
    return (this.authenticationStore.isAgencyUser() && !this.vendor.subscriber) ||
        (this.isPropertyUser && this.vendor.subscriber);
  }

  public saveNonCommPaymentType() {
    this.paymentForm.disable();

    const description = this.paymentForm.value.description;
    const paymentType = this.nonCommType.key;
    const commAmount = this.nonCommAmountPaid;

    const payment = new Payment({
      commissionAmount: commAmount,
      description: description,
      commissionCurrency: this.onyxEvent.currency,
      eventId: this.onyxEvent.eventId,
      eventPartyId: this.vendor.eventPartyId,
      type: paymentType
    });

    const obs$ = this.modifyPayment(payment).pipe(
      tap(() => {
        this.resetPaymentForm();
        const eventId = this.onyxEvent.eventId;

        if (this.isMultiVendor()) {
          this.paymentStore.loadPayments(eventId).subscribe();
          this.paymentStore.loadLatestFinalPayment(eventId).subscribe();
          this.paymentStore.loadFinalPaymentStatusList(eventId).subscribe();
          this.eventStore.getEvent(this.onyxEvent.eventId).subscribe();
          this.adjustmentStore.loadVendorsTotalPaymentsSummary(eventId).subscribe();
        } else {
          this.paymentStore.loadPaymentsByParty(eventId, this.vendor.eventPartyId).subscribe();
          this.paymentStore.loadLatestFinalPaymentByParty(eventId, this.vendor.eventPartyId).subscribe();
          this.paymentStore.loadFinalPaymentStatusList(eventId).subscribe();
          this.eventStore.getEventByParty(this.onyxEvent.eventId, this.vendor.eventPartyId).subscribe();
          this.adjustmentStore.loadVendorsTotalPaymentsSummaryByParty(eventId, this.vendor.eventPartyId).subscribe();
        }
      }),
      catchError(() => {
        return Observable.of(null);
      }),
      finalize(() => {
        this.paymentForm.enable();
      })
    );
    obs$.subscribe();
    return obs$;
  }

  private modifyPayment(payment: Payment): Observable<any> {
    if (this.latestFinalPayment !== null
      && (this.latestFinalPayment.status === PaymentStatus.REQUESTED)
          || (this.latestFinalPayment.status === PaymentStatus.REQUEST_REJECTED)
          || (this.latestFinalPayment.status === PaymentStatus.RESENT_REQUEST)
          || (this.latestFinalPayment.status === PaymentStatus.INVOICE_REJECTED)) {
      return this.paymentStore.updateFinalPayment(payment).finally(() => {
      });
    } else {
      return this.paymentStore.addPayment(payment).finally(() => {
      });
    }
  }

  resetPaymentForm(): void {
    this.paymentForm.reset({
      description: '',
      type: null,
      commissionAmount: undefined
    });
  }

  disablePaidDirect(): boolean {
    if (!this.isPayDirect()) {
      return false;
    }
    if (!this.nonCommAmountPaid || (this.nonCommAmountPaid && this.nonCommAmountPaid < 0)) {
      return true;
    }
    if (!this.paymentForm.valid) {
      return true;
    }
    return false;
  }

  canPaidDirectAndCancel(): boolean {
    return this.isLastPaymentRequestedOrRequestResentOrRequestRejected;
  }

  showPayRejected(): boolean {
    return !this.isLastPaymentPayRejected;
  }

  isSubscriber(): boolean {
    return this.vendor.subscriber;
  }

  getNonCommDescriptionLabel(): string {
    let label = '';
    if (this.isMultiVendor()) {
      label = 'EVENT.COMMISSION.NON_COMM_DESCRIPTION_VENDOR';
    } else {
      label = 'EVENT.COMMISSION.NON_COMM_DESCRIPTION';
    }
    return this.translateService.instant(label);
  }

  isMultiVendor() {
    return this.authenticationStore.hasPermission('MultiVendor');
  }

  checkEventDates() {
    const _endDate = EventUtils.getEventEndDate(this.onyxEvent, this.authenticationStore.isAgencyUser());
    this.isEventFinished = DateUtil.differenceInCalendarDays(DateUtil.parseDate(_endDate), new Date()) < 0;
  }

  onNonCommissionTypeChange() {
    if (this.paymentForm && this.paymentForm.get('commissionAmount')) {
      if (this.isPayDirect()) {
        this.paymentForm.get('commissionAmount').setValue(this.calculatedPaymentWithTax.toFixed(2));
        this.paymentForm.get('commissionAmount').markAsTouched();
      } else {
        this.paymentForm.get('commissionAmount').setValue(null);
        this.paymentForm.get('commissionAmount').markAsUntouched();
      }
    }
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/payment/payment-summary/payment-summary.component.ts